import React, { useEffect, useMemo, useState } from "react";
import GlassCard from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";

/**
 * Operator-friendly page finder.
 * Step 1: scan → Step 2: add new URLs (or you’re already done) → Step 3: go edit.
 */
export default function SEODiscovery({
  job,
  onStart,
  onImport,
  onDone,
  selected,
  onToggle,
  onSelectPaths,
  running,
}) {
  const [filter, setFilter] = useState("new"); // new | all | already
  const results = job?.results || [];
  const status = job?.status;
  const counts = job?.counts || {};
  const newOnes = useMemo(() => results.filter((r) => r.state === "new"), [results]);
  const already = useMemo(() => results.filter((r) => r.state === "managed"), [results]);
  const done = status === "complete";
  const failed = status === "failed";

  useEffect(() => {
    if (!done) return;
    setFilter(newOnes.length > 0 ? "new" : "already");
  }, [done, job?.id, newOnes.length]);

  const visible = useMemo(() => {
    if (filter === "new") return newOnes;
    if (filter === "already") return already;
    return results;
  }, [filter, newOnes, already, results]);

  const selectAllNew = () => onSelectPaths?.(newOnes.map((r) => r.path));

  return (
    <div className="space-y-4 w-full">
      {!done && !running && !failed && results.length === 0 && (
        <GlassCard className="p-4 space-y-3 border border-cyan-500/20 bg-cyan-500/5">
          <p className="text-sm text-white font-medium">What this does</p>
          <ol className="text-xs text-white/60 space-y-2 list-decimal list-inside leading-relaxed">
            <li>Scans your admin content (services, blog, portfolio, nav) and optionally the live sitemap.</li>
            <li>Shows URLs that are <strong className="text-white/80 font-medium">new</strong> (not in SEO yet) vs already added.</li>
            <li>You add the new ones — then edit titles on the Pages list.</li>
          </ol>
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Button onClick={() => onStart(true)} className="btn-gradient text-white border-0 flex-1">
              Find pages (recommended)
            </Button>
            <Button variant="ghost" className="text-white/70 border border-white/10 flex-1" onClick={() => onStart(false)}>
              Admin content only
            </Button>
          </div>
          <p className="text-[11px] text-white/40 leading-relaxed">
            <strong className="text-white/55 font-medium">Find pages</strong> = admin + live site sitemap.{" "}
            <strong className="text-white/55 font-medium">Admin content only</strong> = faster, skips the live site scan.
          </p>
        </GlassCard>
      )}

      {(running || status === "queued" || status === "running") && (
        <GlassCard className="p-5 text-center space-y-2">
          <p className="text-sm text-white">Looking for pages…</p>
          <p className="text-xs text-white/45">This usually takes a few seconds.</p>
        </GlassCard>
      )}

      {failed && (
        <GlassCard className="p-4 border border-red-400/30 bg-red-500/10 space-y-2">
          <p className="text-sm text-red-100">Scan failed</p>
          <p className="text-xs text-red-100/70">{job?.error || "Unknown error"}</p>
          <Button onClick={() => onStart(true)} className="btn-gradient text-white border-0">Try again</Button>
        </GlassCard>
      )}

      {done && (
        <>
          {newOnes.length === 0 ? (
            <GlassCard className="p-5 space-y-3 border border-emerald-400/25 bg-emerald-500/10">
              <p className="text-sm text-emerald-50 font-medium">You’re all set — nothing new to add</p>
              <p className="text-xs text-emerald-100/70 leading-relaxed">
                Found <strong className="font-medium text-emerald-50">{results.length}</strong> URLs
                {counts.from_cms != null ? ` (${counts.from_cms} from admin` : ""}
                {counts.from_live != null ? `, ${counts.from_live} from live site)` : counts.from_cms != null ? ")" : ""}.
                Every one is already in your SEO Pages list.
              </p>
              {job?.remote_error && (
                <p className="text-xs text-amber-100/80">
                  Live site scan had a problem ({job.remote_error}). Admin content still loaded fine.
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={onDone} className="btn-gradient text-white border-0">
                  Go to Pages → edit titles
                </Button>
                <Button variant="ghost" className="text-white/70 border border-white/10" onClick={() => onStart(true)}>
                  Scan again
                </Button>
              </div>
              <p className="text-[11px] text-white/40">Next: open a page → edit Title &amp; description → Save → Publish.</p>
            </GlassCard>
          ) : (
            <GlassCard className="p-5 space-y-3 border border-amber-400/30 bg-amber-400/10">
              <p className="text-sm text-amber-50 font-medium">
                {newOnes.length} new page{newOnes.length === 1 ? "" : "s"} found — add them to SEO
              </p>
              <p className="text-xs text-amber-100/70 leading-relaxed">
                These URLs are on your site but not in SEO yet. Select them and tap Add, then edit their Google titles.
              </p>
              {job?.remote_error && (
                <p className="text-xs text-amber-100/80">
                  Live site scan had a problem ({job.remote_error}). Showing admin content results.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    selectAllNew();
                  }}
                  variant="ghost"
                  className="text-white/80 border border-white/15"
                >
                  Select all new ({newOnes.length})
                </Button>
                <Button
                  onClick={onImport}
                  disabled={!selected.size}
                  className="btn-gradient text-white border-0"
                >
                  Add selected to SEO ({selected.size || 0})
                </Button>
              </div>
            </GlassCard>
          )}

          <div className="flex flex-wrap gap-1.5">
            {[
              { id: "new", label: `New (${newOnes.length})` },
              { id: "already", label: `Already in SEO (${already.length})` },
              { id: "all", label: `All (${results.length})` },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3 h-8 rounded-lg text-xs ${
                  filter === f.id ? "bg-white/12 text-white" : "text-white/45 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              className="text-white/50 ml-auto"
              onClick={() => onStart(true)}
              disabled={running}
            >
              Scan again
            </Button>
          </div>

          <GlassCard className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-white/40">
                <tr>
                  <th className="px-4 py-2 w-10" />
                  <th className="px-4 py-2 text-left">Page path</th>
                  <th className="px-4 py-2 text-left">Found in</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const isNew = r.state === "new";
                  return (
                    <tr key={r.path} className="border-t border-white/5">
                      <td className="px-4 py-2">
                        {isNew ? (
                          <input
                            type="checkbox"
                            checked={selected.has(r.path)}
                            onChange={(e) => onToggle(r.path, e.target.checked)}
                            aria-label={r.path}
                          />
                        ) : (
                          <span className="text-white/20 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-white">{r.path}</td>
                      <td className="px-4 py-2 text-white/50 text-xs">
                        {r.source === "cms" ? "Admin content" : r.source === "sitemap" ? "Live sitemap" : r.source === "internal_link" ? "Navigation" : r.source}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                          isNew ? "bg-amber-400/15 text-amber-100" : "bg-emerald-500/15 text-emerald-200"
                        }`}>
                          {isNew ? "New — add me" : "Already in SEO"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-white/40 text-sm">
                      {filter === "new" ? "No new pages in this filter." : "Nothing to show."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </GlassCard>
        </>
      )}
    </div>
  );
}
